import { Logger, hasAppAuth, simulateLoadingError } from 'whisper-base';
import { BaseWhisperModules, WhisperDependentModules } from './whisper-modules';
import { captureError, captureMessage } from 'whisper-error-tracking';

/**
 * Interface for the Whisper API that will be exposed at window level
 */

const log = new Logger('[whisper-core]');
const initializeWhisperModules = async (instances, modules, isAppAuthEnabled) => {
  // Initialize all whispers
  for (const whisperModule of modules) {
    var _whisperModule$should;
    if (whisperModule.auth === 'app' && !isAppAuthEnabled) {
      log.debug(`Skipping ${whisperModule.name} due to missing app authentication`);
      continue;
    }
    if ((whisperModule === null || whisperModule === void 0 || (_whisperModule$should = whisperModule.shouldLoadModule) === null || _whisperModule$should === void 0 ? void 0 : _whisperModule$should.call(whisperModule)) === false) {
      log.debug(`Skipping ${whisperModule.name} due to shouldLoadModule`);
      continue;
    }
    try {
      log.debug(`Initializing ${whisperModule.name}`);

      // Lazy load the whisper module
      const {
        default: factory
      } = (await whisperModule.loader()) || {};

      // Skip if the module it not exposing a factory
      if (!factory) {
        continue;
      }

      // Create the whisper factory instance
      const whisperFactory = factory();
      const [whisperApi, whisperShutdown] = whisperFactory.initialize();

      // Register the instance shutdown in the whispers map
      instances.set(whisperModule.name, [whisperApi, whisperShutdown]);
    } catch (error) {
      log.error(`Failed to initialize ${whisperModule.name}`, error);
    }
  }
};
const initializeWhisper = async () => {
  simulateLoadingError('WhisperCore');

  // Holds all whisper instances and their shutdown functions
  const instances = new Map();

  // Create the Whisper API object
  const Whisper = {
    debug: enabled => Logger.debug(enabled),
    get: name => {
      const whisper = instances.get(name);
      if (!whisper) {
        log.error(`Whisper ${name} not found`);
        return null;
      }
      return whisper[0];
    },
    shutdown: () => {
      instances.forEach(([, shutdown]) => shutdown());
    },
    captureMessage: (...args) => void captureMessage(...args),
    captureError: (...args) => void captureError(...args)
  };
  log.info('Starting Whisper API initialization');
  const isAppAuthEnabled = hasAppAuth();
  await initializeWhisperModules(instances, BaseWhisperModules, isAppAuthEnabled);

  // Expose the API to the window object
  window.Whisper = Whisper;
  await initializeWhisperModules(instances, WhisperDependentModules, isAppAuthEnabled);

  // Log initialization if logging is enabled
  log.debug('API initialized and exposed at window.Whisper');
};
initializeWhisper().catch(error => {
  log.error('Failed to initialize Whisper API', error);
});